import {
  DynamicSectionComponents,
  DynamicSectionTypes,
  DEFAULT_SECTION_STYLE,
  type TDynamicSection,
  type TSectionStyles,
} from '@/types/dynamic-sections';
import type { RecommendationSectionType } from '@/types/dynamic-sections';
import type { HomePageProps, ISearchProduct, Product } from '@/types';
import type { HomeListContext } from '@/lib/gtm';
import { Grid } from '../products/grid';
import { useTypes } from '@/framework/type';
import { TYPES_PER_PAGE } from '@/framework/client/variables';
import React, { useCallback, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { HeroSlideFallback } from '../image-slider/hero-slide-fallback';
import { resolveHeroSources } from '../image-slider/hero-sources';
import { DynamicProductsWrapper } from '../products/dynamic-products-wrapper';
import { useDynamicSections } from './useDynamicSections';
import DynamicSearchProvider from '@/lib/services/typesense/search-providers/dynamic-provider';
import ProductGridHome from '../products/grids/home';
import HomeSearchProvider from '@/lib/services/typesense/search-providers/home';
import { buildHomeListContext } from '@/lib/gtm';
import { dynamicProductsSectionKey } from '@/lib/utils/dynamic-sections';
import SectionBlock from '../ui/section-block';
import ProductsGrid from '../products/group-products/products-grid';
import ProductsSlider from '../products/group-products/slider';
import dynamic from 'next/dynamic';
import { SectionErrorBoundary } from '@/components/ui/section-error-boundary';
import { ExpressBuyWithPrescriptionCta } from '@/components/prescription-analysis/express-buy-with-prescription-cta';
import { isPrescriptionAnalysisEnabled } from '@/lib/constants/prescription-analysis';

const DynamicRecommendationsWrapper = dynamic(
  () =>
    import('../products/dynamic-recommendations-wrapper').then(
      (m) => m.DynamicRecommendationsWrapper,
    ),
  { ssr: false },
);

const ImageSlider = dynamic(() => import('../image-slider'), { ssr: false });
const DynamicCategoriesMenu = dynamic(
  () =>
    import('../categories/dynamic-categories-menu').then(
      (v) => v.DynamicCategoriesMenu,
    ),
  { ssr: false },
);
const ImageGridLayout = dynamic(() => import('../image-grid'), { ssr: false });
const DeliveryBanner = dynamic(
  () => import('../sections/delivery-banner/delivery-banner'),
  { ssr: false },
);
const PartnerStores = dynamic(
  () => import('../sections/partner-stores/partner-stores'),
  { ssr: false },
);
const LaboratoryPrograms = dynamic(
  () => import('../sections/laboratory-programs/laboratory-programs'),
  { ssr: false },
);
const DynamicLinks = dynamic(
  () => import('../sections/dynamic-links/dynamic-links'),
  { ssr: false },
);

type HeroImageSliderProps = React.ComponentProps<typeof ImageSlider> & {
  /** Preload LCP candidate; only the first home slider should set this. */
  priority?: boolean;
};

/**
 * Paint the first slide from the server (reserved aspect via width/height),
 * then hand off to Swiper. Avoids empty banner CLS while the client chunk loads.
 * While Swiper boots, keep it out of document flow so fallback + slider never stack.
 */
const HeroImageSlider = ({
  priority = false,
  ...props
}: HeroImageSliderProps) => {
  const [sliderReady, setSliderReady] = useState(false);
  const handleReady = useCallback(() => setSliderReady(true), []);
  const fallbackProps = {
    sliders: props.sliders,
    params: props.params,
    renderByBreakpoint:
      props.component === DynamicSectionComponents.IMAGE_SLIDER_BREAKPOINT,
  };
  const hasFallback = resolveHeroSources(fallbackProps).length > 0;
  const showFallback = hasFallback && !sliderReady;

  return (
    <div className="relative">
      {showFallback ? (
        <HeroSlideFallback
          title={props.title}
          styles={props.styles}
          priority={priority}
          uniqueKey={props.uniqueKey}
          {...fallbackProps}
        />
      ) : null}
      <div
        className={showFallback ? 'invisible absolute inset-0 overflow-hidden' : undefined}
        aria-hidden={showFallback || undefined}
      >
        <ImageSlider
          {...props}
          onReady={handleReady}
          skipInitialBlur={hasFallback}
        />
      </div>
    </div>
  );
};

const GRID_CLASS_NAME = 'px-4 pt-3.5 pb-16 lg:p-6 xl:p-8';
const GRID_PRIORITY_COUNT = 4;

const MAP_SHELF_COMPONENTS: {
  [key: string]: React.FunctionComponent<any>;
} = {
  [DynamicSectionComponents.PRODUCTS_GRID]: ProductsGrid,
  [DynamicSectionComponents.PRODUCTS_SLIDER]: ProductsSlider,
};

type CategoryProductsSectionProps = {
  groupId: any;
  categoryIds?: number[];
  variables: any;
  ssrProducts?: ISearchProduct[];
  listContext: HomeListContext;
};

/**
 * InstantSearch renders nothing on the server, so the first page of the grid comes
 * from `getStaticProps` and is swapped for the live grid on its first response.
 */
const CategoryProductsSection = ({
  groupId,
  categoryIds,
  variables,
  ssrProducts,
  listContext,
}: CategoryProductsSectionProps) => {
  const hasServerGrid = Boolean(ssrProducts?.length);
  const [liveReady, setLiveReady] = useState(!hasServerGrid);
  const handleLiveReady = useCallback(() => setLiveReady(true), []);
  const { types: groups } = useTypes({ limit: TYPES_PER_PAGE });
  const categoryName = groups?.find(
    (type: any) => +type?.id === +groupId,
  )?.name;

  return (
    <>
      {liveReady ? null : (
        <Grid
          products={ssrProducts as unknown as Product[]}
          isLoading={false}
          hasMore={false}
          error={null}
          className={GRID_CLASS_NAME}
          categoryName={categoryName}
          listContext={listContext}
          priorityCount={GRID_PRIORITY_COUNT}
        />
      )}
      <HomeSearchProvider group_id={groupId} categoryIds={categoryIds}>
        <ProductGridHome
          className={GRID_CLASS_NAME}
          variables={variables}
          listContext={listContext}
          priorityCount={GRID_PRIORITY_COUNT}
          hideUntilLive={!liveReady}
          onLiveReady={liveReady ? undefined : handleLiveReady}
        />
      </HomeSearchProvider>
    </>
  );
};

type DynamicProductsSectionProps = {
  section: TDynamicSection;
  uniqueKey: string;
  ssrProducts?: ISearchProduct[];
  /** First few shelves hydrate InstantSearch immediately; others wait for near-viewport. */
  deferLiveSearch?: boolean;
};

/**
 * Typesense InstantSearch is client-only (`DynamicSearchProvider` returns null
 * until mount). SSR cards from `getStaticProps` fill the HTML for crawlers and
 * avoid an empty-shelf flash; live hits take over on the first real response.
 */
const DynamicProductsSection = ({
  section,
  uniqueKey,
  ssrProducts,
  deferLiveSearch = false,
}: DynamicProductsSectionProps) => {
  const hasServerShelf = Boolean(ssrProducts?.length);
  const [liveReady, setLiveReady] = useState(!hasServerShelf);
  const handleLiveReady = useCallback(() => setLiveReady(true), []);

  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '400px 0px',
    initialInView: !deferLiveSearch,
  });

  const shouldMountLive = !hasServerShelf || inView || liveReady;

  const component =
    section.component || DynamicSectionComponents.PRODUCTS_GRID;
  const ShelfComponent =
    MAP_SHELF_COMPONENTS[component] || ProductsGrid;
  const styles: TSectionStyles = section.styles || DEFAULT_SECTION_STYLE;
  const listContext = buildHomeListContext({
    id: section.id,
    type: section.type,
    title: section.title,
    order: section.order,
  });

  const ssrShelf =
    !liveReady && hasServerShelf ? (
      section.title ? (
        <SectionBlock style={styles?.title} title={section.title}>
          <ShelfComponent
            uniqueKey={`${uniqueKey}-ssr`}
            products={ssrProducts}
            items={ssrProducts}
            isLoading={false}
            limit={section.items_per_page}
            listContext={listContext}
          />
        </SectionBlock>
      ) : (
        <ShelfComponent
          uniqueKey={`${uniqueKey}-ssr`}
          products={ssrProducts}
          items={ssrProducts}
          isLoading={false}
          limit={section.items_per_page}
          listContext={listContext}
        />
      )
    ) : null;

  return (
    <div ref={ref}>
      {ssrShelf}
      {shouldMountLive ? (
        <DynamicSearchProvider
          filters={section?.filters?.raw || ''}
          ordering={section?.ordering?.raw || ''}
          items_per_page={section?.items_per_page}
        >
          <DynamicProductsWrapper
            {...section}
            uniqueKey={uniqueKey}
            hideUntilLive={!liveReady}
            onLiveReady={liveReady ? undefined : handleLiveReady}
          />
        </DynamicSearchProvider>
      ) : null}
    </div>
  );
};

type DynamicSectionsProps = {
  /** Prefer passing from the page (`getStaticProps`) so sections do not recompute on every router update. */
  variables?: HomePageProps['variables'];
};

export const DynamicSections = ({ variables }: DynamicSectionsProps) => {
  const { sections, variables: resolvedVariables } =
    useDynamicSections(variables);

  const wrapSection = (
    key: string,
    name: string,
    node: React.ReactNode,
  ) => (
    <SectionErrorBoundary key={key} name={name}>
      {node}
    </SectionErrorBoundary>
  );

  let productShelfIndex = 0;
  let hasInjectedExpressBuyCta = false;
  const showExpressBuyCta = isPrescriptionAnalysisEnabled;

  const dynamicSections = sections.map((section, index) => {
    const sectionKey = `${section.type}-${section.component}-${section.order}-${index}`;

    if (section.type === DynamicSectionTypes.DYNAMIC_PRODUCTS) {
      const shelfIndex = productShelfIndex;
      productShelfIndex += 1;
      const shelfKey = dynamicProductsSectionKey(section, shelfIndex);
      return wrapSection(
        sectionKey,
        `products:${section.component}`,
        <DynamicProductsSection
          section={section}
          uniqueKey={shelfKey}
          ssrProducts={resolvedVariables?.ssrShelfProducts?.[shelfIndex]}
          // Keep the first two shelves eager; defer the rest to cut first-paint JS.
          deferLiveSearch={shelfIndex >= 2}
        />,
      );
    }

    if (section.type === DynamicSectionTypes.RECOMMENDATIONS) {
      const recType = section.recommendation_type as
        | RecommendationSectionType
        | undefined;
      if (
        recType &&
        (recType === 'trending' || recType === 'last-seen')
      ) {
        return wrapSection(
          sectionKey,
          `recommendations:${recType}`,
          <DynamicRecommendationsWrapper
            uniqueKey={`${section.type}-${section.component}-${section.order}`}
            component={section.component}
            title={section.title}
            styles={section.styles}
            items_per_page={section?.items_per_page}
            recommendation_type={recType}
            recommendation_params={section.recommendation_params}
            id={section.id}
            type={section.type}
            order={section.order}
          />,
        );
      }
      return null;
    }

    if (section.type === DynamicSectionTypes.PRODUCT_CATEGORIES) {
      return wrapSection(
        sectionKey,
        'categories',
        <DynamicCategoriesMenu
          variables={resolvedVariables?.categories}
          {...section}
        />,
      );
    }

    if (section.type === DynamicSectionTypes.CATEGORIES_PRODUCTS) {
      return wrapSection(
        sectionKey,
        'category-products',
        <CategoryProductsSection
          groupId={resolvedVariables?.group_id}
          categoryIds={resolvedVariables?.typeCategoryIds}
          variables={resolvedVariables?.products || {}}
          ssrProducts={resolvedVariables?.ssrProducts}
          listContext={buildHomeListContext({
            id: section.id,
            type: section.type,
            title: section.title,
            order: section.order,
          })}
        />,
      );
    }

    if (section.type === DynamicSectionTypes.IMAGE_SLIDER) {
      const slider = (
        <HeroImageSlider
          title={section.title}
          styles={section.styles}
          component={section.component as DynamicSectionComponents}
          uniqueKey={`${section.type}-${section.component}-${section.order}-${index}-slider`}
          sliders={section.files}
          params={section.params}
          sectionId={section.id}
          sectionType={section.type}
          sectionTitle={section.title}
          sectionOrder={section.order}
          priority={index === 0}
        />
      );

      const shouldInjectExpressBuy =
        showExpressBuyCta && !hasInjectedExpressBuyCta;
      if (shouldInjectExpressBuy) {
        hasInjectedExpressBuyCta = true;
        return (
          <React.Fragment key={sectionKey}>
            {wrapSection(`${sectionKey}-slider`, 'image-slider', slider)}
            <ExpressBuyWithPrescriptionCta variant="card" />
          </React.Fragment>
        );
      }

      return wrapSection(sectionKey, 'image-slider', slider);
    }

    if (section.type === DynamicSectionTypes.IMAGE_GRID) {
      return wrapSection(
        sectionKey,
        'image-grid',
        <ImageGridLayout {...section} />,
      );
    }

    if (section.type === DynamicSectionTypes.DELIVERY_BANNER) {
      return wrapSection(
        sectionKey,
        'delivery-banner',
        <DeliveryBanner {...section} />,
      );
    }

    if (section.type === DynamicSectionTypes.PARTNER_STORES) {
      return wrapSection(
        sectionKey,
        'partner-stores',
        <PartnerStores
          key={sectionKey}
          sliders={section.files}
          params={section.params}
          {...section}
        />,
      );
    }

    if (section.type === DynamicSectionTypes.LABORATORY_PROGRAMS) {
      return wrapSection(
        sectionKey,
        'laboratory-programs',
        <LaboratoryPrograms
          key={sectionKey}
          sliders={section.files}
          title={section.title}
          {...section}
        />,
      );
    }

    if (section.type === DynamicSectionTypes.DYNAMIC_CATEGORIES_LINKS) {
      return wrapSection(
        sectionKey,
        'dynamic-links',
        <DynamicLinks key={sectionKey} sliders={section.files} {...section} />,
      );
    }

    return null;
  });

  return (
    <>
      <div className="mt-4" />
      {showExpressBuyCta && !hasInjectedExpressBuyCta ? (
        <ExpressBuyWithPrescriptionCta variant="card" />
      ) : null}
      {dynamicSections}
    </>
  );
};
